import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import PlayerLayout from './PlayerLayout';
import PlayerNav from './PlayerNav';
import url from '../../constants/config';
import 'react-quill/dist/quill.snow.css';
import '../ThreadQuill.css';

const getAuthHeader = () => ({
  Authorization: `Bearer ${window.localStorage.getItem('authorization')}`,
});

const PlayerInfoPage = () => {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [tocOpen, setTocOpen] = useState(true);

  useEffect(() => {
    axios
      .get(`${url}/player-portal/info`, { headers: getAuthHeader() })
      .then(res => setContent(res.data.content || ''))
      .catch(err => {
        if (err.response?.status === 404) {
          setError('Przewodnik jest obecnie ukryty dla sportowców.');
          return;
        }
        setError('Nie udało się załadować strony. Spróbuj ponownie.');
      })
      .finally(() => setLoading(false));
  }, []);

  // Reading progress bar
  useEffect(() => {
    if (!content || loading) return;
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
      const scrollable = scrollHeight - clientHeight;
      setProgress(scrollable > 0 ? Math.min(100, (scrollTop / scrollable) * 100) : 0);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [content, loading]);

  // Table of Contents — inject id attributes into headings
  const { toc, processedHtml } = useMemo(() => {
    if (!content) return { toc: [], processedHtml: '' };
    const div = document.createElement('div');
    div.innerHTML = content;
    const headings = Array.from(div.querySelectorAll('h1, h2, h3'));
    const items = headings.map((el, i) => {
      const slug = el.textContent.trim().toLowerCase()
        .replace(/[^\w\s-]/g, '').replace(/\s+/g, '-').slice(0, 50);
      const id = `toc-${i}-${slug}`;
      el.id = id;
      return { level: parseInt(el.tagName[1], 10), text: el.textContent.trim(), id };
    });
    return { toc: items, processedHtml: div.innerHTML };
  }, [content]);

  const scrollToHeading = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <PlayerLayout>
      <PlayerNav />

      {/* Reading progress bar */}
      {!loading && content !== '' && (
        <div style={{
          position: 'fixed', top: 0, left: 0, zIndex: 999,
          height: '3px', background: '#2e496b',
          width: `${progress}%`,
          transition: 'width 0.1s',
          pointerEvents: 'none',
        }} />
      )}

      {loading && (
        <div style={{ textAlign: 'center', padding: '48px', color: '#64748b' }}>
          <i className="fas fa-spinner fa-spin" style={{ fontSize: '24px', marginBottom: '12px', display: 'block' }} />
          Ładowanie...
        </div>
      )}

      {error && (
        <div style={{ color: '#ef4444', padding: '24px', textAlign: 'center' }}>{error}</div>
      )}

      {!loading && !error && content === '' && (
        <div
          style={{
            background: '#ffffff',
            borderRadius: '12px',
            padding: '48px',
            textAlign: 'center',
            color: '#64748b',
            boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
          }}
        >
          <i className="fas fa-book-open" style={{ fontSize: '32px', marginBottom: '12px', display: 'block', color: '#cbd5e1' }} />
          <p style={{ fontSize: '16px', margin: 0 }}>Przewodnik jest jeszcze w przygotowaniu.</p>
          <p style={{ fontSize: '14px', marginTop: '8px' }}>Wróć wkrótce.</p>
        </div>
      )}

      {!loading && !error && content !== '' && (
        <>
          {/* Table of Contents */}
          {toc.length >= 2 && (
            <div style={{
              background: '#ffffff',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              marginBottom: '16px',
              boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
              overflow: 'hidden',
            }}>
              <button
                type="button"
                onClick={() => setTocOpen(o => !o)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 20px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 700,
                  color: '#1e293b',
                }}
              >
                <span>
                  <i className="fas fa-list-ul" style={{ marginRight: '8px', color: '#2e496b' }} />
                  Spis treści
                </span>
                <i className={`fas fa-chevron-${tocOpen ? 'up' : 'down'}`} style={{ color: '#94a3b8', fontSize: '12px' }} />
              </button>

              {tocOpen && (
                <ul style={{ listStyle: 'none', margin: 0, padding: '4px 20px 16px', borderTop: '1px solid #f1f5f9' }}>
                  {toc.map(item => (
                    <li
                      key={item.id}
                      style={{
                        paddingLeft: item.level === 1 ? 0 : item.level === 2 ? '16px' : '32px',
                        marginTop: '6px',
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => scrollToHeading(item.id)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: 0,
                          color: '#2e496b',
                          fontSize: item.level === 3 ? '13px' : '14px',
                          fontWeight: item.level === 1 ? 600 : 400,
                          textAlign: 'left',
                          textDecoration: 'none',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.textDecoration = 'underline'; }}
                        onMouseLeave={e => { e.currentTarget.style.textDecoration = 'none'; }}
                      >
                        {item.text}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Content */}
          <div
            style={{
              background: '#ffffff',
              borderRadius: '12px',
              padding: '32px',
              boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
            }}
          >
            {/* eslint-disable-next-line react/no-danger */}
            <div className="ql-editor" dangerouslySetInnerHTML={{ __html: processedHtml }} />
          </div>
        </>
      )}
    </PlayerLayout>
  );
};

export default PlayerInfoPage;
